// Активность бронирования — верх «Обзора»: полные сутки (МСК) к тому же дню
// недели неделей раньше + недельные/месячные тренды. В разрезе отелей.
// Данные — GET /reports/booking-activity (по дате создания и дате отмены брони TL).
import React, { useState, useEffect } from 'react';
import * as api from '../../utils/api';
import { fmt } from './helpers';
import BookingTrendChart from './BookingTrendChart';

const WD_FULL = ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота'];
const WD_SHORT = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];
const MON_GEN = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
const parseYmd = s => { const [y, m, d] = s.split('-').map(Number); return new Date(Date.UTC(y, m - 1, d)); };
const addDays = (s, n) => { const d = parseYmd(s); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
const dayFull = s => { const d = parseYmd(s); return `${WD_FULL[d.getUTCDay()]}, ${d.getUTCDate()} ${MON_GEN[d.getUTCMonth()]}`; };
const dayShort = s => { const d = parseYmd(s); return `${WD_SHORT[d.getUTCDay()]} ${d.getUTCDate()} ${MON_GEN[d.getUTCMonth()]}`; };

// Навигатор периода ‹ ПОДПИСЬ › — кнопки на фиксированных местах (ширина
// подписи постоянная, «вернуться» резервирует место, даже когда скрыт).
export function PeriodNav({ label, onPrev, onNext, nextDisabled, onReset, resetLabel }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div className="dbx-nav">
        <button type="button" className="dbx-nav__btn" title="Назад" aria-label="Назад" onClick={onPrev}>‹</button>
        <span className="dbx-nav__label">{label}</span>
        <button type="button" className="dbx-nav__btn" title="Вперёд" aria-label="Вперёд" onClick={onNext} disabled={nextDisabled}>›</button>
      </div>
      <button type="button" className="dbx-link" hidden={!onReset} onClick={onReset || undefined}>{resetLabel}</button>
    </div>
  );
}

// Изменение к базе: +12% / −30% / «новое». goodUp=false — рост это плохо (отмены).
function Delta({ cur, prev, goodUp = true }) {
  if (!prev && !cur) return <span className="dbx-delta is-flat">—</span>;
  if (!prev) return <span className={'dbx-delta ' + (goodUp ? 'is-good' : 'is-bad')}>новое</span>;
  // База отрицательная (нетто ушло в минус) — процент бессмысленен, показываем разницу в рублях.
  if (prev < 0) {
    const diff = cur - prev;
    return <span className={'dbx-delta ' + (diff === 0 ? 'is-flat' : (diff > 0) === goodUp ? 'is-good' : 'is-bad')}>{diff >= 0 ? '+' : '−'}{fmt(Math.abs(diff))}</span>;
  }
  const d = (cur - prev) / Math.abs(prev);
  const cls = Math.abs(d) < 0.005 ? 'is-flat' : ((d >= 0) === goodUp ? 'is-good' : 'is-bad');
  return <span className={'dbx-delta ' + cls}>{d >= 0 ? '+' : '−'}{Math.round(Math.abs(d) * 100)}%</span>;
}

export default function BookingActivity() {
  const [date, setDate] = useState(null);            // null — вчера (по умолчанию сервера)
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    const load = () => api.get(`/reports/booking-activity${date ? `?date=${date}` : ''}`)
      .then(d => { if (alive) { setData(d); setErr(''); } })
      .catch(e => { if (alive) setErr(e.message); });
    load();
    // Дашборд держат открытым — освежаем при возврате на вкладку и раз в 10 мин.
    const id = setInterval(load, 10 * 60 * 1000);
    const onVis = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { alive = false; clearInterval(id); document.removeEventListener('visibilitychange', onVis); };
  }, [date]);

  if (err) return <p className="dbx-note" style={{ color: 'var(--jasper-600)' }}>Бронирования: {err}</p>;
  if (!data) return <p className="dbx-note">Загрузка бронирований…</p>;
  return (
    <>
      <DaySection data={data} onDate={setDate} />
      <BookingTrendChart />
    </>
  );
}

// ─── Полные сутки ────────────────────────────────────────────────────────────
function DaySection({ data, onDate }) {
  const isLast = data.day === data.day_max;
  const tiles = [...data.hotels, data.total];
  return (
    <section className="dbx-section">
      <div className="dbx-section__head">
        <div>
          <span className="dbx-kicker">Бронирования за сутки</span>
          <h2 className="dbx-section__title">{dayFull(data.day)}</h2>
          <div className="dbx-section__sub">Полные сутки по Москве · сравнение с <b>{dayShort(data.day_prev)}</b> — тот же день неделей раньше</div>
        </div>
        <PeriodNav
          label={dayShort(data.day)}
          onPrev={() => onDate(addDays(data.day, -1))}
          onNext={() => onDate(addDays(data.day, 1))}
          nextDisabled={isLast}
          onReset={isLast ? null : () => onDate(null)}
          resetLabel="Вчера"
        />
      </div>
      <div className="dbx-day-grid">
        {tiles.map(r => <DayTile key={r.property_id ?? 'total'} r={r} dark={r.property_id == null} />)}
      </div>
      <p className="dbx-note">
        Новые — брони, созданные в эти сутки (включая позже отменённые); отмены — по дате отмены; сумма — вся бронь.
        Ниже каждой цифры — тот же день неделей раньше.
      </p>
    </section>
  );
}

function DayTile({ r, dark }) {
  const c = r.day.cur, p = r.day.prev;
  return (
    <div className={'dbx-card dbx-day' + (dark ? ' dbx-card--dark' : '')}>
      <span className="dbx-hotel">{r.short_name}</span>

      <div className="dbx-day__block">
        <span className="dbx-lbl">Новые брони</span>
        <span className="dbx-big">{fmt(c.new_rev)}<small>{c.new_cnt} шт</small></span>
        <span className="dbx-cmp"><Delta cur={c.new_rev} prev={p.new_rev} /> <span>было {fmt(p.new_rev)}, {p.new_cnt} шт</span></span>
      </div>

      <div className="dbx-day__block">
        <span className="dbx-lbl">Отмены</span>
        <span className="dbx-mid">{fmt(c.canc_rev)}<small>{c.canc_cnt} шт</small></span>
        <span className="dbx-cmp"><Delta cur={c.canc_rev} prev={p.canc_rev} goodUp={false} /> <span>было {fmt(p.canc_rev)}, {p.canc_cnt} шт</span></span>
      </div>

      <div className="dbx-day__block">
        <span className="dbx-lbl" title="Новые брони минус отмены">Нетто</span>
        <span className="dbx-mid" style={c.net_rev < 0 ? { color: dark ? 'var(--jasper-500)' : 'var(--jasper-600)' } : undefined}>{fmt(c.net_rev)}</span>
        <span className="dbx-cmp"><Delta cur={c.net_rev} prev={p.net_rev} /> было {fmt(p.net_rev)}</span>
      </div>
    </div>
  );
}
